import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { FAMILY } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Plus, X, Save, Sparkles } from "lucide-react";

const COMMS = ["phone", "email", "text", "app"];

const RESPITE_SERVICES = [
  ["traditional_respite", "Traditional respite"],
  ["overnight_support", "Overnight support"],
  ["travel_assistance", "Travel assistance"],
  ["party_buffers", "Party buffers"],
  ["community_support", "Community support"],
];

function TextField({ label, value, onChange, testId, textarea, ...rest }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="field-label">{label}</label>
      <Cmp
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        {...rest}
        className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none"
        rows={textarea ? 4 : undefined}
      />
    </div>
  );
}

export default function FamilyProfile() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";
  const adminEditUserId = params.get("admin_edit") || "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    parent_name: "", parent_phone: "",
    family_name: "", address: "", city: "", state: "", zip_code: "",
    preferred_communication: "app",
    home_instructions: "", household_rules: "",
    emergency_contacts: [], pets: [],
    medical_info: "", local_emergency_instructions: "",
    respite_services_needed: [],
  });

  useEffect(() => {
    // Admin editing on behalf of a family loads via the admin endpoint;
    // otherwise the logged-in user's own record.
    const loader = adminEditUserId
      ? api.get(`/admin/families/${adminEditUserId}`).then(({ data }) => data.profile || {})
      : api.get("/families/me").then(({ data }) => data);
    loader.then((data) => {
      setForm({ ...form, ...data });
    }).catch((e) => toast.error(extractError(e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (svc) => setForm((f) => {
    const arr = f.respite_services_needed || [];
    return { ...f, respite_services_needed: arr.includes(svc) ? arr.filter((x) => x !== svc) : [...arr, svc] };
  });

  const addContact = () => setForm((f) => ({
    ...f, emergency_contacts: [...(f.emergency_contacts || []), { _uid: `c-${Date.now()}-${Math.random()}`, name: "", relationship: "", phone: "", email: "" }]
  }));
  const removeContact = (i) => setForm((f) => ({
    ...f, emergency_contacts: f.emergency_contacts.filter((_, idx) => idx !== i)
  }));
  const updateContact = (i, k, v) => setForm((f) => {
    const list = [...f.emergency_contacts]; list[i] = { ...list[i], [k]: v };
    return { ...f, emergency_contacts: list };
  });

  const addPet = () => setForm((f) => ({
    ...f, pets: [...(f.pets || []), { _uid: `p-${Date.now()}-${Math.random()}`, type: "", name: "", notes: "" }]
  }));
  const removePet = (i) => setForm((f) => ({
    ...f, pets: f.pets.filter((_, idx) => idx !== i)
  }));
  const updatePet = (i, k, v) => setForm((f) => {
    const list = [...f.pets]; list[i] = { ...list[i], [k]: v };
    return { ...f, pets: list };
  });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        parent_name: form.parent_name, parent_phone: form.parent_phone,
        family_name: form.family_name, address: form.address, city: form.city,
        state: form.state, zip_code: form.zip_code, lat: form.lat, lng: form.lng,
        preferred_communication: form.preferred_communication,
        home_instructions: form.home_instructions, household_rules: form.household_rules,
        emergency_contacts: (form.emergency_contacts || []).map(({ _uid, ...rest }) => rest),
        pets: (form.pets || []).map(({ _uid, ...rest }) => rest),
        photo_path: form.photo_path,
        medical_info: form.medical_info,
        local_emergency_instructions: form.local_emergency_instructions,
        respite_services_needed: form.respite_services_needed || [],
      };
      // Admin editing routes to the admin endpoint; otherwise the self endpoint.
      if (adminEditUserId) {
        await api.put(`/admin/families/${adminEditUserId}`, payload);
        toast.success("Family profile saved (admin)");
        navigate(`/admin/families/${adminEditUserId}`);
        return;
      }
      await api.put("/families/me", payload);
      toast.success("Family profile saved");
      if (isWelcome) {
        navigate("/family/child/new?welcome=1");
      } else {
        navigate("/family");
      }
    } catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return (<div className="min-h-screen bg-stone2-50"><Navbar /><div className="p-10 text-stone2-500">Loading…</div></div>);

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <span className="section-eyebrow">Household profile</span>
        {adminEditUserId && (
          <div className="mt-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-4 flex items-start gap-3" data-testid="admin-impersonation-banner">
            <Sparkles className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-bold text-amber-900">
                Admin mode — editing on behalf of this family
              </div>
              <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                Anything you save here writes to the family's own record. They'll see these changes next time they log in.
              </p>
              <button type="button" onClick={() => navigate(`/admin/families/${adminEditUserId}`)}
                className="mt-2 text-xs font-semibold text-amber-900 underline hover:text-amber-700"
                data-testid="admin-back-to-family-detail">
                ← Back to family detail
              </button>
            </div>
          </div>
        )}
        {isWelcome && !adminEditUserId && (
          <div className="mt-3 rounded-xl border-2 border-sage-300 bg-sage-50 p-4 flex items-start gap-3" data-testid="family-welcome-banner">
            <Sparkles className="w-5 h-5 text-sage-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-bold text-sage-900">Welcome! Let's get your family set up.</div>
              <p className="mt-1 text-sm text-stone2-700 leading-relaxed">
                Take 5 minutes to fill in the details below. The more sitters know about your family, the better we can match — and the safer every visit will be.
                You can save partial progress at any time and come back to finish later.
              </p>
              <div className="mt-2 text-xs text-stone2-600">
                <strong>What you'll do next:</strong> household info → children → home instructions → emergency contacts.
              </div>
            </div>
          </div>
        )}
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Family details</h1>
        <p className="text-stone2-600 mt-1 text-sm">Sitters see this on booking. Be as clear as possible.</p>

        <form onSubmit={submit} data-testid={FAMILY.familyForm} className="mt-8 space-y-6">
          <section className="paper-card p-6 grid sm:grid-cols-2 gap-5">
            <TextField label="Parent name" value={form.parent_name} onChange={set("parent_name")} testId="family-parent-name" />
            <TextField label="Parent phone" value={form.parent_phone} onChange={set("parent_phone")} testId="family-parent-phone" />
            <TextField label="Family name" value={form.family_name} onChange={set("family_name")} testId="family-family-name" />
            <div>
              <label className="field-label">Preferred communication</label>
              <select value={form.preferred_communication} onChange={(e) => set("preferred_communication")(e.target.value)}
                data-testid="family-pref-comm"
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 outline-none">
                {COMMS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <TextField label="Address" value={form.address} onChange={set("address")} testId="family-address" />
            <TextField label="City" value={form.city} onChange={set("city")} testId="family-city" />
            <TextField label="State" value={form.state} onChange={set("state")} testId="family-state" />
            <TextField label="ZIP code" value={form.zip_code} onChange={set("zip_code")} testId="family-zip" />
          </section>

          <section className="paper-card p-6 space-y-4">
            <h2 className="font-heading font-semibold text-sage-900">Home instructions & rules</h2>
            <TextField textarea label="Home instructions (keys, alarms, routines)" value={form.home_instructions} onChange={set("home_instructions")} testId="family-home-instructions" />
            <TextField textarea label="Household rules" value={form.household_rules} onChange={set("household_rules")} testId="family-household-rules" />
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900">Respite services needed</h2>
            <p className="text-xs text-stone2-500 mt-1">Select the types of support your family is looking for. Sitters offering these services will surface first.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {RESPITE_SERVICES.map(([k, l]) => {
                const active = (form.respite_services_needed || []).includes(k);
                return (
                  <label key={k}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors duration-200 ${
                      active ? "border-sage-600 bg-sage-50" : "border-stone2-200 bg-white hover:border-sage-300"
                    }`}
                    data-testid={`family-respite-${k}`}>
                    <input type="checkbox" checked={active} onChange={() => toggleService(k)}
                      className="rounded text-sage-600 focus:ring-sage-500" />
                    <span className="text-sm text-sage-900">{l}</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="paper-card p-6 space-y-4 border-l-4 border-danger">
            <h2 className="font-heading font-semibold text-sage-900 flex items-center gap-2">
              <span className="text-danger">⚕</span> Medical & emergency preparedness
            </h2>
            <TextField textarea label="Medical info (insurance, pediatrician, allergies to note, dosing)" value={form.medical_info} onChange={set("medical_info")} testId="family-medical-info" />
            <TextField textarea label="Local emergency instructions (nearest ER, poison control, when to call 911)" value={form.local_emergency_instructions} onChange={set("local_emergency_instructions")} testId="family-local-emergency" />
          </section>

          <section className="paper-card p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-sage-900">Emergency contacts</h2>
              <button type="button" onClick={addContact} data-testid={FAMILY.addEmergency} className="btn-outline-sage text-xs">
                <Plus className="w-3.5 h-3.5" /> Add contact
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {(form.emergency_contacts || []).map((c, i) => (
                <div key={c._uid || `ec-idx-${i}`} className="grid sm:grid-cols-4 gap-2 items-start" data-testid={`family-emergency-row-${i}`}>
                  <input placeholder="Name" value={c.name} onChange={(e) => updateContact(i, "name", e.target.value)}
                    className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                  <input placeholder="Relationship" value={c.relationship} onChange={(e) => updateContact(i, "relationship", e.target.value)}
                    className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                  <input placeholder="Phone" value={c.phone} onChange={(e) => updateContact(i, "phone", e.target.value)}
                    className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                  <div className="flex gap-2">
                    <input placeholder="Email" value={c.email} onChange={(e) => updateContact(i, "email", e.target.value)}
                      className="flex-1 rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                    <button type="button" onClick={() => removeContact(i)} className="text-danger p-2 hover:bg-stone2-100 rounded-lg" data-testid={`family-remove-emergency-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {(form.emergency_contacts || []).length === 0 && <p className="text-sm text-stone2-500">Add at least one contact.</p>}
            </div>
          </section>

          <section className="paper-card p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-sage-900">Pets</h2>
              <button type="button" onClick={addPet} data-testid={FAMILY.addPet} className="btn-outline-sage text-xs">
                <Plus className="w-3.5 h-3.5" /> Add pet
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {(form.pets || []).map((p, i) => (
                <div key={p._uid || `pet-idx-${i}`} className="grid sm:grid-cols-4 gap-2" data-testid={`family-pet-row-${i}`}>
                  <input placeholder="Type (dog, cat…)" value={p.type} onChange={(e) => updatePet(i, "type", e.target.value)}
                    className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                  <input placeholder="Name" value={p.name} onChange={(e) => updatePet(i, "name", e.target.value)}
                    className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                  <input placeholder="Notes" value={p.notes} onChange={(e) => updatePet(i, "notes", e.target.value)}
                    className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm sm:col-span-1" />
                  <button type="button" onClick={() => removePet(i)} className="text-danger p-2 hover:bg-stone2-100 rounded-lg justify-self-start" data-testid={`family-remove-pet-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate("/family")} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} data-testid={FAMILY.familySave} className="btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
